import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './PatientTimeline.css';
import AppointmentCard from './AppointmentCard';
import AppointmentModal from './AppointmentModal';
import FormatUrl from '../../utils/UrlFormatter';
import { getClinicId } from '../../utils/config';

const PatientTimeline = (user) => {

  const clinicId = getClinicId(user) 
  const { patientId, doctorId } = useParams();
  // const { patientId } = useParams();
  // const doctorId = new URLSearchParams(window.location.search).get("doctor_id");
  const [appointments, setAppointments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const pageSize = 5;
  const totalAppointments = appointments.length;
  const totalPages = Math.ceil(totalAppointments / pageSize);

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!patientId) {
        setError("Patient ID is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const csrfUrl = FormatUrl(`/csrf-token/`);
        const csrfRes = await fetch(csrfUrl, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!csrfRes.ok) throw new Error('CSRF fetch failed');
        const { csrfToken } = await csrfRes.json();

        const apiUrl = FormatUrl(`/get-patient-timeline/?patient_id=${patientId}&clinic_id=${clinicId}`);
        const res = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
          },
          credentials: 'include',
        });

        if (!res.ok) throw new Error('Failed to fetch appointments');
        const data = await res.json();

        let timelineData = data?.appointments || [];
        if (doctorId) {
          timelineData = timelineData.filter((appt) => appt.doctor_id === parseInt(doctorId));
        }

        setAppointments(timelineData);
        setError('');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [patientId, doctorId, clinicId]);

  const getCurrentPageAppointments = () => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return appointments.slice(start, end);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  

  const currentAppointments = getCurrentPageAppointments();

  return (
    <>
      <div className="timeline-wrapper">
        <div className="timeline-info-box-flex">
          <div className="patient-contact-card">
            <h4>Patient Contact</h4>
            <p><strong>Name:</strong> {appointments[0]?.patient_first_name} {appointments[0]?.patient_last_name}</p>
            <p><strong>Email:</strong> {appointments[0]?.patient_email}</p>
            <p><strong>Phone:</strong> {appointments[0]?.patient_phone}</p>
          </div>

          <div className="timeline-info-main">
            <h3 className="timeline-title">Patient Appointment Timeline</h3>
            <p className="timeline-description">
              This timeline visually represents the chronological history of the patient's appointments.
              You can click on any appointment card to view more details about that specific visit,
              including treatment notes, duration, and assigned practitioner.
            </p>
          </div>
        </div>

        <div className="pat-timeline-container no-border">
      
        {loading ? (
          <div className="timeline-status-pat">
            <div className="timeline-spinner-pat" />
            <p className="timeline-loading-text">Loading Timeline...</p>
          </div>
        ) : error ? (
          <div className="timeline-status-pat">
            <p className="timeline-message error">{error}</p>
          </div>
          ) : (
            <>
              <div className="pat-timeline-list">
                {currentAppointments.map((appt) => (
                  <div
                    className="timeline-row-pat"
                    key={appt.appointment_id}
                    onClick={() => setSelectedAppointment(appt)}
                  >
                    <div className="timeline-marker-pat">
                      <div className="timeline-line-pat" />
                      <div className="timeline-dot-wrapper">
                        <div className="timeline-dot-pat" />
                        <div className="timeline-horizontal-line-pat" />
                      </div>
                    </div>
                    <AppointmentCard appointment={appt} />
                  </div>
                ))}
              </div>
      
              {totalPages > 1 && (
                <div className="pat-pagination">
                  <button
                    className="pat-pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
      
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        className={`pat-pagination-btn ${page === currentPage ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
      
                  <button
                    className="pat-pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
      
          <AppointmentModal
            appointment={selectedAppointment}
            onClose={() => setSelectedAppointment(null)}
          />
        </div>
      </div>
    </>
  );
  
};

export default PatientTimeline;
